<script setup lang="ts">
/**
 * « Acquitter » une alarme à traiter, depuis la liste, la fiche ou le détail
 * (geste 4 `acquitter_depuis_l_ecran`, route 7 ; `boutonAcquitter`
 * S:4888-4898).
 *
 * Ce que l'utilisateur doit vivre : un destinataire de l'alarme dit « je m'en
 * occupe » ; les rappels s'arrêtent pour tout le monde. Rien ne bascule à
 * l'écran avant la réponse : la ligne dit « Acquittée par … » seulement quand
 * le serveur l'a dit, en nommant le PREMIER acquitteur (un second appui, ou un
 * collègue plus rapide, rend 200 avec lui), puis la surveillance est relue.
 * Une alarme revenue à la normale entre-temps se dit par sa phrase. Un
 * double clic n'envoie qu'une requête.
 *
 * Le bouton n'existe que sur une alarme à traiter ouverte et non acquittée.
 * Ne pas être destinataire le laisse inactif avec la phrase de la matrice ;
 * des destinataires non servis le cachent (on ne sait pas).
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cibleAlarme,
    useActionsStore,
} from "@modules/alarmes-v2/stores/actions.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2Acquittement,
    AlarmesV2Alarme,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"
import { nomDeLaPersonne } from "@modules/alarmes-v2/utils/journal"
import { formaterInstant } from "@modules/alarmes-v2/utils/mesure"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** L'alarme ouverte, ou `null`. */
        alarme: AlarmesV2Alarme | null
        /** Sa surveillance, pour savoir si la personne en est destinataire. */
        surveillance: AlarmesV2Surveillance | null
        /** Relit ce que l'écran montre ; vrai si la relecture a abouti. */
        relire: () => Promise<boolean>
        /** Applique la réponse du serveur (le détail d'une alarme). */
        appliquer?: (reponse: AlarmesV2Acquittement) => void
        /** Écrire l'aide « Acquitter, c'est dire je m'en occupe ». */
        avecAide?: boolean
        /** Les classes du bouton. */
        classe?: string
    }>(),
    {
        appliquer: undefined,
        avecAide: true,
        classe: "rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white",
    }
)

const auth = useAuthStore()
const actions = useActionsStore()
const { acces } = useAcces()

// ================================
// COMPUTED
// ================================
/** Une alarme à traiter, ouverte et non acquittée : la seule qui s'acquitte. */
const getAAcquitter = computed((): boolean => {
    const a = props.alarme
    if (!a?._id) return false
    if (a.nature === "pour_information") return false
    // Un acquitteur servi : déjà acquittée, quel que soit le mot de l'état.
    if (a.prise_en_compte) return false
    return a.etat === "declenchee_non_prise_en_compte"
})

/**
 * Être destinataire de la surveillance : `true`, `false`, ou `null` quand
 * les destinataires ne sont pas servis (le geste se tait).
 */
const getDestinataire = computed((): boolean | null => {
    const d = props.surveillance?.destinataires
    if (!Array.isArray(d) || !auth.userId) return null
    return d.some((x) => x?._id_user === auth.userId)
})

const getAcces = computed(() =>
    acces("acquitter_depuis_l_ecran", getDestinataire.value)
)

const getCible = computed(() =>
    props.alarme?._id ? cibleAlarme(props.alarme._id) : ""
)

/** Ce que dit le succès : le premier acquitteur, nommé par le serveur. */
const getTexteFait = computed((): string => {
    const e = getCible.value ? actions.etat(getCible.value) : null
    if (!e || e.statut !== "fait" || e.geste !== "acquitter") return ""
    const r = (e.valeur ?? {}) as AlarmesV2Acquittement
    const prise = r.prise_en_compte
    if (!prise) return ""
    const nom = nomDeLaPersonne(prise.par, r.personnes, tr)
    const quand = formaterInstant(prise.le, tr) ?? ""
    return prise.par && prise.par !== auth.userId
        ? t("alarmesV2.actions.fait.acquitterDeja", { nom, quand })
        : t("alarmesV2.actions.fait.acquitter", { nom, quand })
})

/** Montrer l'annonce : une écriture sur cette alarme a eu lieu. */
const getAAnnonce = computed(
    () => !!getCible.value && !!actions.etat(getCible.value)
)

// ================================
// HANDLERS
// ================================
/** Acquitte : UNE requête, puis la relecture. */
async function onAcquitter(): Promise<void> {
    const alarme = props.alarme
    if (!alarme?._id || !getAcces.value.ok) return
    const issue = await actions.ecrire({
        cible: cibleAlarme(alarme._id),
        geste: "acquitter",
        appel: () => serviceAlarmesV2.alarmesV2Acquitter(alarme._id),
        relire: props.relire,
    })
    if (issue.issue === "fait" && props.appliquer) props.appliquer(issue.valeur)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <span
        v-if="getAAcquitter || getAAnnonce"
        class="inline-flex max-w-full flex-col items-start gap-1"
        data-test="acquitter"
    >
        <BoutonGeste
            v-if="getAAcquitter"
            :acces="getAcces"
            :libelle="t('alarmesV2.actions.acquitter')"
            :classe="classe"
            @click="onAcquitter"
        />
        <span
            v-if="getAAcquitter && avecAide && getAcces.affichage !== 'absent'"
            class="text-xs text-surface-600"
            data-test="aide-acquitter"
            >{{ t("alarmesV2.actions.aideAcquitter") }}</span
        >
        <AnnonceEcriture
            v-if="getAAnnonce"
            :cible="getCible"
            :texte-fait="getTexteFait"
            :relire="relire"
        />
    </span>
</template>
